import {
	ClientPortfolioDomainEnum,
	type ClientPortfolioPaymentFrequencyEnum,
	type CurrencyEnum,
	type PortfolioListItemResponseDTO,
	type PortfolioListReportingSummaryResponseDTO,
} from "web_myplann/Infrastructure/Api/Api";
import { REPORTING_VALUE_DOMAINS } from "web_myplann/Portfolio/Helpers/portfolioReportingDomains";

export type TileAmount = {
	amount: number;
	frequency?: ClientPortfolioPaymentFrequencyEnum | null;
};

export const TILE_SECONDARY_KIND = {
	evaluation: "evaluation",
	payment: "payment",
} as const;

export type TileSecondary =
	| { kind: typeof TILE_SECONDARY_KIND.evaluation; percent: number }
	| ({ kind: typeof TILE_SECONDARY_KIND.payment } & TileAmount);

export type PortfolioTileTrailing = {
	primary: TileAmount | null;
	secondary: TileSecondary | null;
	isMissingValue: boolean;
	currency?: CurrencyEnum;
};

const DEBT_DOMAINS: ClientPortfolioDomainEnum[] = [
	ClientPortfolioDomainEnum.Loans,
	ClientPortfolioDomainEnum.BusinessLoans,
	ClientPortfolioDomainEnum.Leasing,
];

const PAYMENT_DOMAINS: ClientPortfolioDomainEnum[] = [
	ClientPortfolioDomainEnum.PropertyInsurance,
	ClientPortfolioDomainEnum.Services,
	ClientPortfolioDomainEnum.PaidAdvisory,
];

const toAmount = (
	amount: number | null | undefined,
	frequency?: ClientPortfolioPaymentFrequencyEnum | null,
): TileAmount | null =>
	amount === null || amount === undefined ? null : { amount, frequency };

const toEvaluation = (
	percent: number | null | undefined,
): TileSecondary | null =>
	percent === null || percent === undefined
		? null
		: { kind: TILE_SECONDARY_KIND.evaluation, percent };

const toPayment = (amount: TileAmount | null): TileSecondary | null =>
	amount === null ? null : { kind: TILE_SECONDARY_KIND.payment, ...amount };

export const getPortfolioTileTrailing = (
	data: Pick<
		PortfolioListItemResponseDTO,
		| "id"
		| "domain"
		| "payment"
		| "installment"
		| "paymentFrequency"
		| "hasInvestmentTool"
	>,
	summaryData: PortfolioListReportingSummaryResponseDTO[] | undefined,
): PortfolioTileTrailing => {
	const summary = summaryData?.find((entry) => entry.id === data.id)?.summary;
	const toTrailing = (
		primary: TileAmount | null,
		secondary: TileSecondary | null,
		isMissingValue: boolean,
	): PortfolioTileTrailing => ({
		primary,
		secondary,
		isMissingValue,
		currency: summary?.currency,
	});
	const value = toAmount(
		summary?.totalValue === undefined ? null : Math.round(summary.totalValue),
	);
	const payment = toAmount(
		data.payment ?? data.installment,
		data.paymentFrequency,
	);
	const isSummaryPending =
		summaryData === undefined &&
		!!data.domain &&
		REPORTING_VALUE_DOMAINS.includes(data.domain);

	if (data.domain && DEBT_DOMAINS.includes(data.domain)) {
		const installment = toAmount(
			data.installment ?? data.payment,
			data.paymentFrequency,
		);
		return toTrailing(
			value,
			toPayment(installment),
			!isSummaryPending && value === null,
		);
	}

	if (data.domain && PAYMENT_DOMAINS.includes(data.domain)) {
		return toTrailing(payment, null, payment === null);
	}

	if (
		data.domain === ClientPortfolioDomainEnum.LifeInsurance &&
		!data.hasInvestmentTool &&
		value === null
	) {
		return toTrailing(payment, null, !isSummaryPending && payment === null);
	}

	if (value === null) {
		return toTrailing(payment, null, !isSummaryPending);
	}

	return toTrailing(
		value,
		toEvaluation(summary?.totalEvaluationPercent),
		false,
	);
};
